import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import ExcelJS from "exceljs";
import { FormattedMessage } from "react-intl";
import moment from "moment";

import "./Shipment.scss";
import { ReactComponent as CSV } from "assets/images/icons/TopNavIcons/CSV.svg";
import { ReactComponent as XLSX } from "assets/images/icons/TopNavIcons/XLSX.svg";
import getLabel from "helpers/getLabel";
import { toCsvRow } from "helpers/spreadsheetHelpers";
import testIds from "constants/testIds";
import { ReactComponent as ExportIcon } from "assets/images/icons/TopNavIcons/ExportIcon.svg";

interface IExportList {
  title: string;
  testId: string;
  icon: JSX.Element;
  format: string;
}

interface FormattedItem {
  [key: string]: any;
}

interface FlattenedItem {
  [key: string]: string | FlattenedItem;
}

const hiddenKeys = [
  "display_status",
  "freight_mode_type",
  "is_off_platform_shipment",
  // Below are keys from the origin and delivery objects
  "origin_origin_city",
  "origin_origin_country",
  "quoted_pickup_datetime",
  "updated_pickup_datetime",
  "schedule_dates_no_conversion",
  "delivery_destination_city",
  "delivery_destination_country",
  "origin_origin_coordinates_latitude",
  "origin_origin_coordinates_longitude",
  "delivery_destination_coordinates_latitude",
  "delivery_destination_coordinates_longitude",
  "quoted_delivery_datetime",
  "updated_delivery_datetime",
];

const orderingKeys = ["shipment_reference", "status", "has_exception"];

function ExportTable({ shipmentData }: any) {
  const numberOfResults = shipmentData?.sortedRows.length;
  const exportList: IExportList[] = [
    { title: "Export to CSV", testId: testIds.SHIPMENT_DASHBOARD_EXPORT_REQUEST_CSV_BUTTON, icon: <CSV />, format: "csv" },
    {
      title: "Export to XSLX",
      testId: testIds.SHIPMENT_DASHBOARD_EXPORT_REQUEST_PDF_BUTTON,
      icon: <XLSX />,
      format: "xlsx",
    },
  ];

  const formatHeader = (key: string) => {
    let formattedKey = key;

    // Replace "stop1" with "destination"
    if (key.includes("stop1")) formattedKey = key.replace("stop1", "destination");

    // Handle custom field labels (custom_fields, cost_center, reason_code, etc.)
    if (key.startsWith("custom_field_")) {
      const fieldNumber = key.replace("custom_field_", "");
      formattedKey = getLabel(`Custom field ${fieldNumber}`, "capitalize", "", true);
    }
    if (key === "cost_center") formattedKey = getLabel("Cost Center / WBS / Project #", "capitalize", "", true);
    if (key === "reason_code") formattedKey = getLabel("Reason code", "capitalize", "", true);

    if (key === "has_exception") formattedKey = "Exception";

    // Formats the headers by removing the _ and capitalizing the first letters.
    return formattedKey
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Flatten the object so that all values can be exported to excel
  function flattenObject(obj: Record<string, any>, parentKey = ""): FlattenedItem {
    return Object.keys(obj).reduce((acc: FlattenedItem, key) => {
      // Determine the new key
      const newKey = parentKey ? `${parentKey}_${key}` : key;

      // Check if the key should be hidden
      if (hiddenKeys.includes(newKey)) {
        return acc; // Skip hidden keys
      }

      // Handle special cases for "destination_name" and "origin_name"
      if (key === "destination_name" || key === "origin_name") {
        acc[key] = obj[key];
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        // Recursively flatten nested objects
        Object.assign(acc, flattenObject(obj[key], newKey));
      } else {
        // Default behavior: Assign the key with or without the parent key prefix
        acc[newKey] = obj[key];
      }

      return acc;
    }, {});
  }

  // Transforms the data to match the formatted headers
  const transformData = (data: any) =>
    data.map((item: any) => {
      const flattenedItem = flattenObject(item);
      const orderedItem = Object.fromEntries(
        orderingKeys
          .map((key) => [key, flattenedItem[key]])
          .concat(Object.entries(flattenedItem).filter(([key]) => !orderingKeys.includes(key))),
      );

      const formattedItem: FormattedItem = {};

      Object.keys(orderedItem).forEach((key) => {
        const formattedKey = formatHeader(key);
        // Hide pickup, stop1 dates and zipcodes for off-platform shipments
        if (
          item.is_off_platform_shipment === true &&
          ["pickup_date", "stop1_date", "pickup_zipcode", "stop1_zipcode"].includes(key)
        ) {
          formattedItem[formattedKey] = "-";
        } else if (key === "has_exception") {
          formattedItem[formattedKey] = item.has_exception === null ? "N/A" : item.has_exception ? "Yes" : "No";
        } else {
          formattedItem[formattedKey] = orderedItem[key];
        }
      });

      return formattedItem;
    });

  const exportData = (format: string) => {
    const now = moment();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Data");

    const transformedData = transformData(shipmentData?.sortedRows.map((row: any) => row.original) || []);

    if (format === "xlsx") {
      const headers = Object.keys(transformedData[0]);
      worksheet.columns = headers.map((header) => ({ header, key: header, width: 15 }));

      transformedData.forEach((row: any) => {
        worksheet.addRow(row);
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `ShipmentData_${now.format("DD-MM-YYYY_HH:mm")}.xlsx`;
        a.click();

        URL.revokeObjectURL(url);
      });
    }

    if (format === "csv") {
      const headers = Object.keys(transformedData[0]);
      const csvData = [toCsvRow(headers)];

      transformedData.forEach((row: any) => {
        csvData.push(toCsvRow(Object.values(row)));
      });

      const blob = new Blob([csvData.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `ShipmentData_${now.format("DD-MM-YYYY_HH:mm")}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    }
  };

  const popover = (
    <Popover
      id="popover-trigger-click"
      className="export-popover"
      style={{
        width: "191px",
        border: "none",
        padding: "16px",
        boxShadow: "0px 2px 8px rgba(0,0,0, 0.15)",
      }}
    >
      <Popover.Content style={{ padding: 0 }}>
        {numberOfResults === 0 && (
          <div className="font-weight-bold text-center" style={{ fontSize: "12px" }}>
            <FormattedMessage id="components.molecules.navigation.noExportData" defaultMessage="No exportable data found" />
          </div>
        )}
        {numberOfResults > 0 && (
          <div>
            <div className="font-weight-bold text-center" style={{ fontSize: "15px" }}>
              <FormattedMessage
                id="components.molecules.navigation.exportResults"
                defaultMessage="Export {results} results"
                values={{ results: numberOfResults }}
              />
            </div>
            <div className="d-flex flex-column">
              {exportList.map(({ title, testId, icon, format }) => (
                <div
                  data-testid={testId}
                  className="export-tab-item"
                  key={title}
                  onClick={() => {
                    exportData(format);
                  }}
                >
                  {icon}
                  <span className="ml-2">{`Export to ${format.toUpperCase()}`}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Popover.Content>
    </Popover>
  );

  return (
    <div className="position-relative mr-3">
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 0 }}
        overlay={
          <Tooltip id="">
            <FormattedMessage id="components.molecules.navigation.export" defaultMessage="Export" />
          </Tooltip>
        }
      >
        <div>
          <OverlayTrigger trigger="click" placement="bottom-end" overlay={popover} rootClose>
            <ExportIcon style={{ cursor: "pointer" }} data-testid={testIds.SHIPMENT_DASHBOARD_EXPORT_BUTTON} />
          </OverlayTrigger>
        </div>
      </OverlayTrigger>
    </div>
  );
}

export default ExportTable;
